import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, ShoppingCart, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import { getProductImage } from '@/utils/imageUtils';
import { getCart as getStoredCart, setCart as saveCart } from '@/utils/cartStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Wishlist() {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadWishlist = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(res.data.items || []);
    } catch (error) {
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    loadWishlist();
  }, [navigate, loadWishlist]);

  const removeFromWishlist = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/wishlist/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(wishlist.filter(item => item.product_id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const addToCart = (product) => {
    const cart = getStoredCart();
    const existing = cart.find(item => item.product_id === product.product_id);
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }
    saveCart(cart);
    toast.success('Added to cart');
    window.dispatchEvent(new Event('cartUpdated'));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Marketplace
            </Button>
            <Logo />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Heart className="w-8 h-8 text-[#00F0FF]" />
          <h1 className="text-3xl font-bold">My Wishlist</h1>
          <span className="text-neutral-400">({wishlist.length} items)</span>
        </div>

        {wishlist.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 text-neutral-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Your wishlist is empty</h2>
            <p className="text-neutral-400 mb-6">Save products you&apos;re interested in for later</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlist.map((product) => (
              <div
                key={product.product_id}
                className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-[#00F0FF]/30 transition-all group"
              >
                <div
                  className="aspect-video bg-neutral-800 relative cursor-pointer"
                  onClick={() => navigate(`/product/${product.product_id}`)}
                >
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=400&fit=crop&auto=format'; }}
                    />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromWishlist(product.product_id);
                    }}
                    className="absolute top-3 right-3 p-2 bg-black/50 rounded-full hover:bg-red-500/80 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4">
                  <h3
                    className="font-semibold mb-1 cursor-pointer hover:text-[#00F0FF] transition-colors line-clamp-1"
                    onClick={() => navigate(`/product/${product.product_id}`)}
                  >
                    {product.name}
                  </h3>
                  {product.creator && (
                    <p className="text-sm text-neutral-400 mb-3">
                      by {product.creator.store_name}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-[#00F0FF]">
                      ${product.price?.toFixed(2)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => addToCart(product)}
                      className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default Wishlist;
